import { useEffect, useState, useRef, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";

//hooks
import { useDebounceHook } from "../../utils/useDebounceHook";

//bootstrap
import { Row, Col, Form } from "react-bootstrap";

//ui componentes
import FormField from "../../ui/FormField";
import FormSelectSearchAsyncField from "../../ui/FormSelectSearchAsyncField";
import FormSelect from "../../ui/FormSelect";
import ErrorMessage from "../../ui/ErrorMessage";

//static option data for select fields with fixed options (yes/no, etc.)
import formSelectOptions from "../../../config/formSelectOptions";

//services
import {
  useGetProjectSplosnoDetails,
  useGetSifrantProjektov,
  useGetSifrantRazpisov,
  useGetSifrantArrsCerifKlasius,
  useGetPodrocjaSifrantForRazpis,
  useEditProjectSplosnoDetails,
} from "../../../services/ProjectDetails/splosnoAPI";

//form validation schema
import { zodResolver } from "@hookform/resolvers/zod";
import splosnoFormSchema from "../../../config/splosnoFormSchema";

const SplosnoForm = ({ setIsEditing, onDirtyChange }) => {
  const { id } = useParams();
  const prevRazpis = useRef();

  const [searchTermProjektov, setSearchTermProjektov] = useState("");
  const [searchTermRazpisov, setSearchTermRazpisov] = useState("");

  // We memoize the schema to avoid unnecessary re-renders and validation resets in useForm when the schema reference changes
  const schema = useMemo(() => splosnoFormSchema, []);

  const {
    register,
    reset,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
  });

  // Watch fields from Form for conditional logic
  const sodStudenti = useWatch({ control, name: "sodStudenti" });
  const obcanskaZnanost = useWatch({ control, name: "obcanskaZnanost" });
  const obcanskaZnanostKrovniProjekt = useWatch({
    control,
    name: "obcanskaZnanostKrovniProjekt",
  });
  const razpisSelected = useWatch({ control, name: "razpisSelected" });
  const obcanskaZnanostSelectedProjekt = useWatch({
    control,
    name: "obcanskaZnanostSelectedProjekt",
  });

  // Notify parent whenever dirty state changes for enabling/disabling Save button and showing unsaved changes modal
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // -------------------
  // Fetch data (sifrants and project details)
  // -------------------
  const { data: project } = useGetProjectSplosnoDetails(id);
  const { data: sifrantiArrsCerifKlasiusData } =
    useGetSifrantArrsCerifKlasius();
  const { data: sifrantProjektov, isLoading: isLoadingSifrant } =
    useGetSifrantProjektov(useDebounceHook(searchTermProjektov, 300));
  const { data: sifrantRazpisov, isLoading: isLoadingSifrantRazpisov } =
    useGetSifrantRazpisov(useDebounceHook(searchTermRazpisov, 300));
  const { data: sifrantPodrocjaForRazpis = {} } =
    useGetPodrocjaSifrantForRazpis(razpisSelected?.value);

  // -------------------
  // Edit project details
  // -------------------
  const {
    mutate: editProjectSplosnoDetails,
    isPending: isPendingEdit,
    isError: isError,
    error,
    reset: resetMutationError,
  } = useEditProjectSplosnoDetails();

  /* 
     Initialize form values when project details are fetched
     We also set razpisSelected and obcanskaZnanostSelectedProjekt for AsyncSelect fields based on project details
  */
  useEffect(() => {
    if (!project) return;

    reset({
      naziv: project.naziv || "",
      akronim: project.akronim || "",
      sifra: project.sifra || "",
      zacetek: project.zacetek || "",
      konec: project.konec || "",
      pogodba: project.pogodba || "",
      referencnaSt: project.referencnaSt || "",
      spletnaStran: project.spletnaStran || "",
      deminimis: project.deminimis,
      sodGopodarstvom: project.sodGopodarstvom,
      sodStudenti: project.sodStudenti,
      stStudenti: project.stStudenti || 0,
      enakostSpolov: project.enakostSpolov,
      povzetek: project.povzetek || "",
      obcanskaZnanost: project.obcanskaZnanost,
      obcanskaZnanostKrovniProjekt: project.obcanskaZnanostKrovniProjekt,
      obcanskaZnanostSelectedProjekt: project.obcanskaZnanostKrovniProjekt
        ? {
            value: project.obcanskaZnanostIdProjekt,
            label: project.obcanskaZnanostNazivProjekt,
          }
        : null,
      razpisSelected: project.razpisID
        ? { value: project.razpisID, label: project.razpis }
        : null,
      cerifID: project.cerifID,
      arrsID: project.arrsID,
      klasiusID: project.klasiusID,
    });
  }, [project, reset]);

  /* 
    Dependent field logic inside UseEffect 
     -sodStudenti -> stStudenti
     -obcanskaZnanost -> obcanskaZnanostKrovniProjekt -> obcanskaZnanostSelectedProjekt
     -obcanskaZnanostKrovniProjekt -> obcanskaZnanostSelectedProjekt
     -razpisSelected -> cerifID, arrsID, klasiusID
  */
  useEffect(() => {
    if (sodStudenti === false) setValue("stStudenti", 0, { shouldDirty: true });
  }, [sodStudenti, setValue]);

  useEffect(() => {
    if (obcanskaZnanost === false) {
      setValue("obcanskaZnanostKrovniProjekt", false, { shouldDirty: true });
      setValue("obcanskaZnanostSelectedProjekt", null, { shouldDirty: true });
    }
  }, [obcanskaZnanost, setValue]);

  useEffect(() => {
    if (obcanskaZnanostKrovniProjekt === false) {
      setValue("obcanskaZnanostSelectedProjekt", null, { shouldDirty: true });
    }
  }, [obcanskaZnanostKrovniProjekt, setValue]);

  useEffect(() => {
    // On initial load, just set the ref without resetting dependent fields
    if (!prevRazpis.current) {
      prevRazpis.current = razpisSelected;
      return;
    }

    // reset cerifID, arrsID, klasiusID if we select a different razpis than before
    if (prevRazpis.current?.value !== razpisSelected?.value) {
      ["cerifID", "arrsID", "klasiusID"].forEach((field) => {
        setValue(field, 0, { shouldDirty: true });
      });
    }

    prevRazpis.current = razpisSelected;
  }, [razpisSelected, setValue]);

  // -------------------
  // Submit handler
  // -------------------
  const onSubmit = (data) => {
    // Prepare payload for API call based on form values
    const payload = {
      id: parseInt(id),
      naziv: data.naziv,
      akronim: data.akronim,
      sifra: data.sifra,
      zacetek: data.zacetek,
      konec: data.konec,
      pogodba: data.pogodba,
      referencnaSt: data.referencnaSt,
      spletnaStran: data.spletnaStran,
      deminimis: data.deminimis,
      sodGopodarstvom: data.sodGopodarstvom,
      sodStudenti: data.sodStudenti,
      stStudenti: data.stStudenti,
      enakostSpolov: data.enakostSpolov,
      povzetek: data.povzetek,
      obcanskaZnanost: data.obcanskaZnanost,
      obcanskaZnanostKrovniProjekt: data.obcanskaZnanostKrovniProjekt,
      razpisID: data.razpisSelected?.value || null,
      obcanskaZnanostIdProjekt:
        data.obcanskaZnanostSelectedProjekt?.value || null,
      cerifID: data.cerifID,
      arrsID: data.arrsID,
      klasiusID: data.klasiusID,
    };

    editProjectSplosnoDetails(payload, {
      onSuccess: () => setIsEditing(false),
      onError: () => setTimeout(() => resetMutationError(), 3000),
    });
  };

  return (
    <div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        {/* ── Osnovni podatki ── */}
        <h6 className="form-section-heading">Osnovni podatki</h6>
        <Row className="mb-2">
          <Col md={12}>
            <FormField
              label="Naslov projekta"
              formControlType="text"
              register={register("naziv")}
              validationError={errors.naziv?.message}
            />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col md={6}>
            <FormField
              label="Akronim projekta"
              formControlType="text"
              register={register("akronim")}
            />

            <FormField
              label="Šifra projekta"
              formControlType="text"
              register={register("sifra")}
            />
          </Col>
          <Col md={6}>
            <FormField
              label="Začetek projekta"
              formControlType="date"
              register={register("zacetek")}
              validationError={errors.zacetek?.message}
            />
            <FormField
              label="Konec projekta"
              formControlType="date"
              register={register("konec")}
              validationError={errors.konec?.message}
            />
          </Col>
        </Row>

        <hr className="form-section-divider" />

        {/* ── Pogodba in sodelovanje ── */}
        <h6 className="form-section-heading">Pogodba in sodelovanje</h6>
        <Row className="mb-4">
          <Col md={6}>
            <FormField
              label="Pogodba"
              formControlType="text"
              register={register("pogodba")}
            />
            <FormField
              label="Referenčna številka"
              formControlType="text"
              register={register("referencnaSt")}
            />
            <FormField
              label="Spletna stran projekta"
              formControlType="text"
              register={register("spletnaStran")}
            />
          </Col>
          <Col md={6}>
            <FormSelect
              label="Deminimis"
              name="deminimis"
              control={control}
              options={formSelectOptions}
            />
            <FormSelect
              label="Sodelovanje s gospodarstvom"
              name="sodGopodarstvom"
              control={control}
              options={formSelectOptions}
            />
            <FormSelect
              label="Sodelovanje s študenti"
              name="sodStudenti"
              control={control}
              options={formSelectOptions}
            />
            {sodStudenti && (
              <FormField
                label="Število sodelujočih študentov"
                formControlType="number"
                register={register("stStudenti")}
              />
            )}
            <FormSelect
              label="Enakost spolov"
              name="enakostSpolov"
              control={control}
              options={formSelectOptions}
            />
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={12}>
            <FormField
              label="Povzetek projekta"
              formControlType="textarea"
              register={register("povzetek")}
            />
          </Col>
        </Row>

        <hr className="form-section-divider" />

        {/* ── Občanska znanost ── */}
        <h6 className="form-section-heading">Občanska znanost</h6>
        <Row className="mb-4">
          <Col md={6}>
            <FormSelect
              label="Projekt občanske znanosti"
              name="obcanskaZnanost"
              control={control}
              options={formSelectOptions}
            />
            {obcanskaZnanost && (
              <FormSelect
                label="Krovni projekt občanske znanosti"
                name="obcanskaZnanostKrovniProjekt"
                control={control}
                options={formSelectOptions}
              />
            )}
            {obcanskaZnanost && obcanskaZnanostKrovniProjekt && (
              <FormSelectSearchAsyncField
                label="Izberite krovni projekt"
                name="obcanskaZnanostSelectedProjekt"
                control={control}
                options={sifrantProjektov || []}
                setSearchTerm={setSearchTermProjektov}
                placeholder="Izberi projekt"
                isLoading={isLoadingSifrant}
                validationError={errors.obcanskaZnanostSelectedProjekt?.message}
              />
            )}
          </Col>
        </Row>

        <hr className="form-section-divider" />

        {/* ── Razpis in klasifikacije ── */}
        <h6 className="form-section-heading">Razpis in klasifikacije</h6>
        <Row className="mb-4">
          <Col md={6}>
            <FormSelectSearchAsyncField
              label="Razpis"
              name="razpisSelected"
              control={control}
              options={sifrantRazpisov || []}
              setSearchTerm={setSearchTermRazpisov}
              placeholder="Izberi razpis"
              isLoading={isLoadingSifrantRazpisov}
              validationError={errors.razpisSelected?.message}
            />

            {sifrantPodrocjaForRazpis?.sifrantCERIF && (
              <FormSelect
                label="CERIF"
                name="cerifID"
                control={control}
                options={sifrantiArrsCerifKlasiusData?.cerif || []}
                validationError={errors.cerifID?.message}
              />
            )}
            {sifrantPodrocjaForRazpis?.sifrantARRS && (
              <FormSelect
                label="ARRS"
                name="arrsID"
                control={control}
                options={sifrantiArrsCerifKlasiusData?.arrs || []}
                validationError={errors.arrsID?.message}
              />
            )}
            {sifrantPodrocjaForRazpis?.sifrantKLASIUS && (
              <FormSelect
                label="KLASIUS"
                name="klasiusID"
                control={control}
                options={sifrantiArrsCerifKlasiusData?.klasius || []}
                validationError={errors.klasiusID?.message}
              />
            )}
          </Col>
        </Row>

        {/* ── Submit ── */}
        <div className="d-flex pt-2 pb-3">
          <button
            type="submit"
            className="btn btn-primary px-4"
            disabled={isPendingEdit || !isDirty}
          >
            {isPendingEdit ? "Shranjujem..." : "Shrani"}
          </button>
        </div>
      </Form>

      {isError && (
        <ErrorMessage message={error?.message || "Napaka pri shranjevanju"} />
      )}
    </div>
  );
};

export default SplosnoForm;
